import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useQueryClient} from '@github-ui/react-query'
import type {AddRelatesToMutationMutation} from '@github-ui/server-persisted-queries/types/addRelatesToMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {dependenciesPickerRelationshipsQueryKey} from '../components/sections/relations-section/fetch-dependencies-picker-relationships'
import {RELATES_TO_CONNECTION_PATH} from '../components/sections/relations-section/relates-to-query-types'
import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {OptimisticDependencyIssue} from './add-blocked-by-mutation'

type AddRelatesToVariables = {
  issueId: string
  nameWithOwner: string
  issueNumber: number
}

type AddRelatesToOperation = Extract<ConnectionOperation, {type: 'addToConnection'}>

function toOptimisticOperation(relatedIssue: OptimisticDependencyIssue): AddRelatesToOperation {
  const [owner, name] = relatedIssue.repository.nameWithOwner.split('/')
  return {
    type: 'addToConnection',
    connectionPath: RELATES_TO_CONNECTION_PATH,
    anchor: {at: 'end'},
    counts: [{path: [...RELATES_TO_CONNECTION_PATH, 'totalCount'], delta: 1}],
    node: {
      ...relatedIssue,
      titleHTML: escapeOptimisticTitleHTML(relatedIssue.title),
      repository: {...relatedIssue.repository, name, owner: {login: owner}},
    },
  }
}

async function addRelatesToRequest({
  nameWithOwner,
  issueNumber,
  relatedIssueId,
}: AddRelatesToVariables & {relatedIssueId: string}): Promise<AddRelatesToMutationMutation> {
  const response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/relates_to.json`, {
    method: 'POST',
    body: {relatedIssueId},
  })
  const result = (await response.json()) as {
    data?: AddRelatesToMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message ?? result.data?.addRelatesTo?.errors[0]?.message
  if (!response.ok || error || !result.data?.addRelatesTo?.issue) {
    throw new Error(error ?? 'Failed to add related issue')
  }
  return result.data
}

export function useAddRelatesToMutation({issueId, nameWithOwner, issueNumber}: AddRelatesToVariables) {
  const queryClient = useQueryClient()
  const [owner, repo] = nameWithOwner.split('/')
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner ?? '', repo ?? '', issueNumber)
  const pickerQueryKey = dependenciesPickerRelationshipsQueryKey(nameWithOwner, issueNumber)

  const {mutateAsync} = useConnectionMutation<AddRelatesToMutationMutation>({
    ownerId: issueId,
    connectionPath: RELATES_TO_CONNECTION_PATH,
    mutationFn: operation =>
      addRelatesToRequest({
        nameWithOwner,
        issueNumber,
        issueId,
        relatedIssueId: (operation as AddRelatesToOperation).node.id,
      }),
    onSuccess: async data => {
      const issue = data.addRelatesTo?.issue
      if (issue) {
        await settleConfirmedOperation({
          queryClient,
          targets: [{queryKey: secondaryQueryKey}],
          ownerId: issueId,
          operation: {type: 'setFields', id: issue.id, fields: {topRelatesTo: issue.topRelatesTo}},
        })
      }
      await queryClient.invalidateQueries({queryKey: pickerQueryKey})
    },
  })

  return useCallback(
    (relatedIssue: OptimisticDependencyIssue) => mutateAsync({operation: toOptimisticOperation(relatedIssue)}),
    [mutateAsync],
  )
}
